import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import LightDashboard from '../../assets/landing_page_images/Light-Dashboard.webp';
import DarkDashboard from '../../assets/landing_page_images/Dark-Dashboard.webp';
import LightDocument from '../../assets/landing_page_images/Light-Document.webp';
import DarkDocument from '../../assets/landing_page_images/Dark-Document.webp';
import LightDocumentUpload from '../../assets/landing_page_images/Light-DocumentUploadFlow.webp';
import DarkDocumentUpload from '../../assets/landing_page_images/Dark-DocumentUploadFlow.webp';
import LightChat from '../../assets/landing_page_images/Light-Chat.webp';
import DarkChat from '../../assets/landing_page_images/Dark-Chat.webp';
import SeoPageLayout from './SeoPageLayout';
import useSeoMeta from './useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import { APP_URLS } from '../../helpers/url_helper';
import './WHSComplianceSoftware.css';

const CAPABILITIES = [
  {
    title: 'WHS Documents and Policies',
    icon: 'folder',
    paragraphs: [
      'Store policies, procedures, SWMS, risk assessments, WHS plans, registers and other compliance records in a central document library.',
      'You can manage document versions, review dates and expiry dates, while filters and search help teams locate current information when needed.',
    ],
  },
  {
    title: 'Digital WHS Forms and QR Codes',
    icon: 'form',
    paragraphs: [
      'Create company forms and templates for workers and teams to complete digitally. Forms can include dynamic fields, signatures and links to employees, projects, sites, assets and tasks.',
      <>
        Create and manage{' '}
        <Link
          to={APP_URLS.DIGITAL_WHS_FORMS}
          className="section-description-link"
        >
          digital WHS forms
        </Link>
        {' '}and QR codes to make inspections, reporting and compliance workflows easier.
      </>,
    ],
  },
  {
    title: 'Audits and Corrective Actions',
    icon: 'audit',
    paragraphs: [
      'Complimate helps turn audit findings and compliance gaps into assigned tasks.',
      <>
        Complete compliance audits, identify findings and manage{' '}
        <Link
          to={APP_URLS.WHS_COMPLIANCE_AUDIT_SOFTWARE}
          className="section-description-link"
        >
          corrective actions
        </Link>
        {' '}with assigned owners, due dates and evidence.
      </>,
    ],
  },
  {
    title: 'Incidents and Hazards',
    icon: 'hazard',
    paragraphs: [
      'Manage incident and hazard-related information alongside your wider WHS workflow. A finding from an inspection can lead to a corrective action, assigned responsibility and follow-up task rather than remaining only as a checklist item.',
    ],
  },
  {
    title: 'Sites, Assets and People',
    icon: 'sites',
    paragraphs: [
      'Maintain site records with site-specific documents and digital form submissions. Complimate also provides registers for vehicles, plant, equipment and tools, including service intervals, registration expiry and condition information.',
      'Employee profiles, employee documents, submitted forms and role-based permissions help businesses assign responsibilities and control access to different areas of the platform.',
    ],
  },
];

const WORKFLOW_STEPS = [
  {
    title: '1. Store your documents',
    icon: 'folder',
    text: 'Keep WHS policies, procedures, SWMS, registers and other records organised.',
  },
  {
    title: '2. Complete digital forms and inspections',
    icon: 'form',
    text: 'Workers can access forms through the platform or QR codes and submit records from the field.',
  },
  {
    title: '3. Identify findings and actions',
    icon: 'search',
    text: 'Use audits, inspections, incidents and other workflows to record issues and compliance gaps.',
  },
  {
    title: '4. Assign responsibility',
    icon: 'users',
    text: 'Turn findings into tasks with owners, priorities, deadlines and supporting evidence.',
  },
  {
    title: '5. Monitor progress',
    icon: 'dashboard',
    text: 'Use the dashboard, task views and notifications to see what is due, overdue or completed.',
  },
];

const AUDIENCE = [
  { text: 'WHS and HSE managers', icon: 'shield' },
  { text: 'Compliance officers', icon: 'audit' },
  { text: 'Quality managers', icon: 'report' },
  { text: 'Site supervisors', icon: 'hardhat' },
  { text: 'Operations managers', icon: 'dashboard' },
  { text: 'WHS consultants', icon: 'users' },
  { text: 'Small and medium businesses', icon: 'business' },
  { text: 'Organisations managing multiple sites', icon: 'pin' },
];

const DIFFERENTIATORS = [
  { text: 'WHS document management', icon: 'folder' },
  { text: 'Digital WHS forms', icon: 'form' },
  { text: 'Audits and inspections', icon: 'audit' },
  { text: 'Incidents and hazards', icon: 'hazard' },
  { text: 'Corrective actions', icon: 'task' },
  { text: 'Task management', icon: 'project' },
  { text: 'SWMS and risk assessments', icon: 'shield' },
  { text: 'Employee and contractor compliance', icon: 'users' },
  { text: 'Training and inductions', icon: 'training' },
  { text: 'Plant and asset registers', icon: 'plant' },
  { text: 'Site management', icon: 'pin' },
  { text: 'Return-to-work processes', icon: 'return' },
  { text: 'Chain of Responsibility', icon: 'link' },
  { text: 'ISO management system support', icon: 'badge' },
  { text: 'AI-assisted WHS and compliance functionality', icon: 'spark' },
];

const FAQ_ITEMS = [
  {
    question: 'What is WHS compliance software?',
    answer:
      'WHS compliance software is a digital platform used to organise and manage workplace health and safety processes, documentation, inspections, audits, actions and compliance records.',
  },
  {
    question: 'Can Complimate manage WHS documents?',
    answer:
      'Yes. Complimate provides a searchable document hub for policies, procedures, SWMS, registers, WHS plans and compliance records, with version and expiry tracking.',
  },
  {
    question: 'Can Complimate manage digital WHS forms?',
    answer:
      'Yes. Businesses can create digital forms, use templates, capture signatures, connect forms to sites or projects and provide QR-code access for workers.',
  },
  {
    question: 'Can Complimate track corrective actions?',
    answer:
      "Yes. Complimate's project and task functionality supports assigned actions, due dates, priorities, attachments, comments and completion tracking.",
  },
  {
    question: 'Is Complimate available across Australia?',
    answer:
      'Yes. Complimate is positioned as an Australia-wide platform, with the SEO strategy focused on national Australian terms rather than limiting the product to a specific state or city.',
  },
  {
    question: 'Does Complimate provide legal WHS advice?',
    answer:
      'Complimate provides software and AI-assisted compliance functionality. AI-generated information should not be represented as legal advice or as a substitute for appropriate professional advice.',
  },
];

const { path: PAGE_PATH, title: PAGE_TITLE, description: PAGE_DESCRIPTION } =
  getPublicSeoPage('/whs-compliance-software/');

const ICON_PATHS = {
  shield: (
    <>
      <path d="M12 3 5 6v5c0 4.6 2.9 7.6 7 10 4.1-2.4 7-5.4 7-10V6l-7-3Z" />
      <path d="m8.5 12 2.2 2.2 4.8-5" />
    </>
  ),
  folder: (
    <>
      <path d="M3 6h7l2 2h9v11H3V6Z" />
      <path d="M3 10h18" />
    </>
  ),
  form: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  audit: (
    <>
      <path d="M8 4h8M9 3v3h6V3M6 5H4v16h16V5h-2" />
      <path d="m8 13 2.2 2.2L16 9.5" />
    </>
  ),
  hazard: (
    <>
      <path d="M12 3 22 20H2L12 3Z" />
      <path d="M12 9v5M12 17h.01" />
    </>
  ),
  sites: (
    <>
      <path d="M4 21V8l8-5 8 5v13M9 21v-6h6v6" />
      <path d="M8 10h.01M12 10h.01M16 10h.01" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m16 16 4 4" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6M15 15c3 0 5 1.7 5.5 5" />
    </>
  ),
  dashboard: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="5" rx="1.5" />
      <rect x="13" y="10" width="8" height="11" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
    </>
  ),
  report: (
    <>
      <path d="M6 3h8l4 4v14H6V3ZM14 3v5h5" />
      <path d="M9 17v-3M12 17v-6M15 17v-4" />
    </>
  ),
  hardhat: <path d="M4 14a8 8 0 0 1 16 0M3 14h18v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3ZM12 4v3" />,
  business: (
    <>
      <path d="M4 21V7h10v14M14 11h6v10M8 11h2M8 15h2M8 19h2M17 15h.01M17 19h.01" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </>
  ),
  task: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="m8 12 2.4 2.4L16 9" />
    </>
  ),
  project: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 2v4M16 2v4M3 9h18M7 13h4M7 17h8" />
    </>
  ),
  training: (
    <>
      <path d="m2 8 10-5 10 5-10 5L2 8Z" />
      <path d="M6 10.5V16c3.5 2.6 8.5 2.6 12 0v-5.5" />
    </>
  ),
  plant: (
    <>
      <path d="M3 16h12v-5H8.5L6 8H3v8Z" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="13" cy="18" r="2" />
      <path d="M15 13h3.5L21 16v2h-6" />
    </>
  ),
  return: (
    <>
      <path d="m9 7-5 5 5 5M4 12h9a6 6 0 0 1 6 6" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0-7.07-7.07L10 5.93" />
      <path d="M14 11a5 5 0 0 0-7.07 0L5.52 12.41a5 5 0 0 0 7.07 7.07L14 18.07" />
    </>
  ),
  badge: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  spark: (
    <>
      <path d="m12 3 1.3 4.2L17.5 9l-4.2 1.8L12 15l-1.3-4.2L6.5 9l4.2-1.8L12 3Z" />
      <path d="m19 15 .6 1.9 1.9.6-1.9.6L19 21l-.6-1.9-1.9-.6 1.9-.6L19 15Z" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 8h.01" />
    </>
  ),
};

function FeatureIcon({ name }) {
  return (
    <span className="whs-feature-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[name] || ICON_PATHS.shield}
      </svg>
    </span>
  );
}

function WHSComplianceSoftware() {
  const [openFaq, setOpenFaq] = useState(0);
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  useSeoMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: PAGE_PATH,
  });

  return (
    <SeoPageLayout>
      <div className="whs-page">
        <section className="whs-hero">
          <Container>
            <div className="whs-hero-grid">
              <div className="whs-hero-copy">
                <p className="seo-eyebrow">
                  <FeatureIcon name="shield" />
                  WHS Compliance Software
                </p>
                <h1 className="seo-h1">
                  WHS Compliance Software for Australian Businesses
                </h1>
                <p className="seo-lead">
                  Complimate is WHS compliance software designed to help Australian
                  businesses manage work health and safety activities in one place.
                  It connects documents, digital forms, audits, incidents,
                  corrective actions, tasks, sites, assets and team responsibilities
                  into one practical compliance workflow.
                </p>
                <p className="seo-body">
                  Built alongside an Australian WHS consulting business, Complimate
                  is designed around how businesses manage safety and compliance in
                  day-to-day operations.
                </p>
                <div className="seo-cta-row">
                  <Button
                    as={Link}
                    to="/contact-us"
                    className="btn-teal whs-primary-button"
                    id="whs_book_demo_hero"
                  >
                    Book a Demo
                    <span aria-hidden="true">→</span>
                  </Button>
                </div>
              </div>
              <div className="whs-hero-visual">
                <div className="whs-visual-glow" aria-hidden="true" />
                <div className="whs-product-window">
                  <div className="whs-window-bar" aria-hidden="true">
                    <span /><span /><span />
                  </div>
                  <img
                    src={isDark ? DarkDashboard : LightDashboard}
                    alt="Complimate WHS compliance dashboard for tasks, documents and alerts"
                    width="960"
                    height="680"
                    fetchPriority="high"
                  />
                </div>
                <div className="whs-float-card whs-float-card-top" aria-hidden="true">
                  <FeatureIcon name="folder" />
                  <span>WHS documents</span>
                </div>
                <div className="whs-float-card whs-float-card-bottom" aria-hidden="true">
                  <FeatureIcon name="task" />
                  <span>Corrective actions</span>
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section whs-intro-section">
          <Container>
            <div className="whs-intro-grid">
              <div>
                <div className="whs-section-marker"><FeatureIcon name="shield" /></div>
                <h2 className="seo-h2">What Is WHS Compliance Software?</h2>
                <p className="seo-body">
                  WHS compliance software helps businesses organise and manage their
                  workplace health and safety processes digitally instead of relying
                  on disconnected spreadsheets, documents, emails and paper forms.
                </p>
                <p className="seo-body">
                  Complimate provides a central platform for managing WHS
                  documentation, inspections, audits, incidents, corrective actions,
                  digital forms, employee records, contractor compliance, sites and
                  assets.
                </p>
                <p className="seo-body">
                  The platform is designed for businesses that want to manage their{' '}
                  <Link
                    to={APP_URLS.SAFETY_MANAGEMENT_SOFTWARE}
                    className="section-description-link"
                  >
                    safety management
                  </Link>{' '}
                  systems with clearer responsibilities, accessible records and a
                  connected workflow.
                </p>
              </div>
              <div className="whs-shot-frame">
                <img
                  src={isDark ? DarkDocument : LightDocument}
                  alt="Complimate central WHS document and compliance record library"
                  width="960"
                  height="680"
                />
              </div>
            </div>
          </Container>
        </section>

        <section className="seo-section whs-capabilities-section">
          <Container>
            <div className="whs-capabilities-intro">
              <div className="whs-shot-frame">
                <img
                  src={isDark ? DarkDocumentUpload : LightDocumentUpload}
                  alt="Complimate digital WHS forms and compliance workflow"
                  width="960"
                  height="680"
                />
              </div>
              <div>
                <div className="whs-section-marker"><FeatureIcon name="form" /></div>
                <h2 className="seo-h2">What Can You Manage With Complimate?</h2>
                <p className="seo-body">
                  Complimate brings key WHS and compliance activities together so
                  information can move from one part of the workflow to another.
                </p>
              </div>
            </div>
            <div className="whs-capability-grid">
              {CAPABILITIES.map((item, index) => (
                <article className="whs-capability-card" key={item.title}>
                  <div className="whs-card-topline">
                    <FeatureIcon name={item.icon} />
                    <span>0{index + 1}</span>
                  </div>
                  <h3 className="seo-h3">{item.title}</h3>
                  {item.paragraphs.map((paragraph, paragraphIndex) => (
                    <p key={`${item.title}-${paragraphIndex}`} className="seo-body">
                      {paragraph}
                    </p>
                  ))}
                </article>
              ))}
            </div>
          </Container>
        </section>

        <section className="seo-section whs-cta-section">
          <Container>
            <div className="whs-cta-card">
              <span className="whs-cta-orb whs-cta-orb-one" aria-hidden="true" />
              <span className="whs-cta-orb whs-cta-orb-two" aria-hidden="true" />
              <div className="whs-cta-copy">
                <h2 className="seo-h2">Manage WHS Compliance in One Place</h2>
                <p className="seo-body">
                  Bring your WHS documents, digital forms, audits, corrective
                  actions, tasks and compliance records together with Complimate.
                </p>
              </div>
              <Button
                as={Link}
                to="/contact-us"
                className="btn-teal whs-cta-button"
                id="whs_book_demo_mid"
              >
                Book a Demo
                <span aria-hidden="true">→</span>
              </Button>
            </div>
          </Container>
        </section>

        <section className="seo-section whs-workflow-section">
          <Container>
            <div className="whs-section-intro">
              <h2 className="seo-h2">How Does Complimate Help Manage WHS Compliance?</h2>
              <p className="seo-body">
                Complimate connects different compliance activities into a single
                workflow:
              </p>
            </div>
            <div className="whs-steps">
              {WORKFLOW_STEPS.map((step, index) => (
                <article key={step.title} className="whs-step">
                  <div className="whs-step-number">{index + 1}</div>
                  <FeatureIcon name={step.icon} />
                  <div>
                    <h3 className="seo-h4">{step.title}</h3>
                    <p className="seo-body mb-0">{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
            <div className="whs-note">
              <FeatureIcon name="link" />
              <p className="seo-body mb-0">
                This connected approach is one of Complimate&apos;s core
                differentiators: the platform is intended to connect hazards,
                incidents, forms, documents, audits and corrective actions rather
                than treating each activity as a separate system.
              </p>
            </div>
          </Container>
        </section>

        <section className="seo-section whs-audience-section">
          <Container>
            <div className="whs-section-intro">
              <h2 className="seo-h2">Who Is Complimate WHS Software For?</h2>
              <p className="seo-body">
                Complimate is designed for Australian organisations that need to
                manage WHS and compliance across people, documents, tasks and
                locations.
              </p>
              <p className="seo-body">It is particularly relevant to:</p>
            </div>
            <ul className="whs-audience-grid">
              {AUDIENCE.map((item) => (
                <li className="whs-audience-card" key={item.text}>
                  <FeatureIcon name={item.icon} />
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
            <p className="seo-body whs-centered-note">
              The platform&apos;s current product messaging focuses on
              Construction, Childcare, Health, Not-for-Profit and Local
              Government.
            </p>
          </Container>
        </section>

        <section className="seo-section whs-difference-section">
          <Container>
            <div className="whs-difference-intro">
              <div>
                <div className="whs-section-marker"><FeatureIcon name="spark" /></div>
                <h2 className="seo-h2">What Makes Complimate Different?</h2>
                <p className="seo-body">
                  Complimate is positioned as a practical Australian WHS software
                  and compliance management software platform that can support a
                  complete safety system rather than only inspections or checklists.
                </p>
                <p className="seo-body">Its documented capabilities include:</p>
              </div>
              <div className="whs-shot-frame">
                <img
                  src={isDark ? DarkChat : LightChat}
                  alt="Complimate AI Compliance Assistant for WHS guidance"
                  width="960"
                  height="680"
                />
              </div>
            </div>
            <ul className="whs-difference-grid">
              {DIFFERENTIATORS.map((item) => (
                <li className="whs-difference-card" key={item.text}>
                  <FeatureIcon name={item.icon} />
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
            <div className="whs-note">
              <FeatureIcon name="info" />
              <p className="seo-body mb-0">
                Complimate also includes an AI Compliance Assistant that can answer
                questions using company documents and provide guidance mapped to WHS
                legislation, ISO standards and government requirements. AI-generated
                recommendations and documents should be treated as assistance rather
                than legal advice.
              </p>
            </div>
          </Container>
        </section>

        <section className="seo-section whs-faq-section" id="faq">
          <Container>
            <div className="seo-section-header text-center">
              <h2 className="seo-h2 seo-faq-heading">Frequently Asked Questions</h2>
            </div>
            <div className="seo-faq-list">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.question}
                    className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      type="button"
                      className="seo-faq-question"
                      aria-expanded={isOpen}
                      onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    >
                      <h3 className="seo-h5">{item.question}</h3>
                      <span className="seo-faq-icon" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && <p className="seo-faq-answer seo-body">{item.answer}</p>}
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      </div>
    </SeoPageLayout>
  );
}

export default WHSComplianceSoftware;
